import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { toast } from 'react-toastify';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import { ChevronDown } from 'lucide-react';
import { ANTIBOT_CONFIG, ANTIBOT_HEADERS } from '../../config/antibot';

const ACQUISITION_SOURCES = ['Email', 'TikTok', 'LinkedIn', 'Facebook', 'Google', 'Recommandation', 'Autre'];

const BRAND_RED = '#c70039';

interface CaptchaChallenge {
  challenge_id: string;
  question: string;
}

interface SignupVerifyResponse {
  valid: boolean;
  reason?: string;
}

const Register = () => {
  const [searchParams] = useSearchParams();
  const urlRole = searchParams.get('role'); // 'demandeur' | 'prestataire' | null

  const initialRole = urlRole === 'prestataire' ? 'provider' : urlRole === 'demandeur' ? 'requester' : null;

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'requester' | 'provider' | null>(initialRole);
  const [profession, setProfession] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acquisitionSource, setAcquisitionSource] = useState('');

  // Anti-bot: captcha challenge state
  const [captchaChallengeId, setCaptchaChallengeId] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);

  // Anti-bot: honeypot field (hidden from real users)
  const [websiteUrl, setWebsiteUrl] = useState('');

  const { register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const generateCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    setCaptchaError(false);
    try {
      const response = await fetch(ANTIBOT_CONFIG.generateUrl, {
        method: 'POST',
        headers: ANTIBOT_HEADERS,
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = (await response.json()) as CaptchaChallenge;
      setCaptchaChallengeId(data.challenge_id);
      setCaptchaQuestion(data.question);
      setCaptchaAnswer('');
    } catch (error) {
      console.error('Failed to generate captcha challenge:', error);
      setCaptchaError(true);
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  useEffect(() => {
    generateCaptcha();
  }, [generateCaptcha]);

  // Derive contextual strings from URL role param
  const roleCtx = useMemo(() => {
    if (urlRole === 'demandeur') {
      return {
        title: t('register.role.demandeur.title'),
        subtitle: t('register.role.demandeur.subtitle'),
        professionLabel: t('register.role.demandeur.professionLabel'),
        professionPlaceholder: t('register.role.demandeur.professionPlaceholder'),
        submitLabel: t('register.role.demandeur.submit'),
      };
    }
    if (urlRole === 'prestataire') {
      return {
        title: t('register.role.prestataire.title'),
        subtitle: t('register.role.prestataire.subtitle'),
        professionLabel: t('register.role.prestataire.professionLabel'),
        professionPlaceholder: t('register.role.prestataire.professionPlaceholder'),
        submitLabel: t('register.role.prestataire.submit'),
      };
    }
    return null;
  }, [urlRole, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username || !email || !confirmEmail || !password || !acquisitionSource) {
      toast.error('All fields are required');
      return;
    }

    if (!role) {
      toast.error('Veuillez sélectionner un rôle');
      return;
    }

    if (email !== confirmEmail) {
      toast.error('Emails do not match');
      return;
    }

    if (!acceptedTerms) {
      toast.error('You must accept the Terms of Service to register');
      return;
    }

    if (!captchaAnswer) {
      toast.error('Veuillez répondre à la question anti-bot');
      return;
    }

    try {
      setLoading(true);

      // Call n8n signup-verify webhook for anti-bot validation
      const verifyResponse = await fetch(ANTIBOT_CONFIG.verifyUrl, {
        method: 'POST',
        headers: ANTIBOT_HEADERS,
        body: JSON.stringify({
          challenge_id: captchaChallengeId,
          user_answer: captchaAnswer,
          honeypot_value: websiteUrl,
          email,
          username,
          role,
          profession,
          acquisition_source: acquisitionSource,
        }),
      });

      if (!verifyResponse.ok) {
        throw new Error(`HTTP ${verifyResponse.status}`);
      }

      const verifyData = (await verifyResponse.json()) as SignupVerifyResponse;

      if (!verifyData.valid) {
        const reason = verifyData.reason || '';

        if (reason.includes('rate') || reason.includes('limit') || reason.includes('trop')) {
          toast.error('Trop de tentatives, veuillez réessayer dans quelques minutes.');
        } else if (reason.includes('captcha') || reason.includes('wrong') || reason.includes('incorrect')) {
          toast.error('Réponse incorrecte, veuillez réessayer.');
          generateCaptcha();
        } else {
          toast.error('Une erreur est survenue, veuillez réessayer.');
        }
        return;
      }

      // Verification passed — proceed with existing Firestore client-side user creation
      await register(email, confirmEmail, password, username, role);

      try {
        await addDoc(collection(db, 'acquisition_sources'), {
          user_id: auth.currentUser?.uid ?? null,
          source: acquisitionSource,
          created_at: serverTimestamp(),
        });
      } catch (err) {
        console.error('Failed to save acquisition source:', err);
      }

      if (role === 'requester') {
        navigate('/requester/profile');
      } else {
        navigate('/provider/profile');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      toast.error('Une erreur est survenue, veuillez réessayer.');
      generateCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-colors bg-white';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-10 px-4">
      {/* Logo */}
      <div className="mb-6 text-center">
        <img
          src="/Logo_OpportunAi-01.png"
          alt="OpportunAI"
          className="h-12 mx-auto mb-3"
          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
        <p className="text-sm text-gray-500">La plateforme des artisans et entrepreneurs</p>
      </div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Header bar */}
        <div style={{ background: BRAND_RED }} className="px-6 py-4">
          <h1 className="text-xl font-bold text-white text-center">
            {roleCtx ? roleCtx.title : 'Créer un compte'}
          </h1>
          {roleCtx && (
            <p className="text-sm text-white/85 text-center mt-1">{roleCtx.subtitle}</p>
          )}
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mt-2">
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center">
                <span className="text-[10px] font-bold" style={{ color: BRAND_RED }}>1</span>
              </div>
              <span className="text-xs text-white font-medium">Compte</span>
            </div>
            <div className="w-8 h-px bg-white/40" />
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-white/30 flex items-center justify-center">
                <span className="text-[10px] font-bold text-white">2</span>
              </div>
              <span className="text-xs text-white/60">Profil</span>
            </div>
          </div>
        </div>

        <div className="px-6 py-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.username')}
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                className={inputClass}
                style={{ '--tw-ring-color': BRAND_RED } as React.CSSProperties}
                required
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.email')}
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className={inputClass}
                required
              />
            </div>

            {/* Confirm Email */}
            <div>
              <label htmlFor="confirmEmail" className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.confirmEmail')}
              </label>
              <input
                type="email"
                id="confirmEmail"
                value={confirmEmail}
                onChange={e => setConfirmEmail(e.target.value)}
                className={inputClass}
                required
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.password')}
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className={inputClass}
                required
                minLength={6}
              />
            </div>

            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.iAmA')}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setRole('requester')}
                  className="py-2.5 px-3 rounded-lg border-2 text-sm font-medium transition-all"
                  style={
                    role === 'requester'
                      ? { background: BRAND_RED, color: '#fff', borderColor: BRAND_RED }
                      : { background: '#fff', color: '#374151', borderColor: '#e5e7eb' }
                  }
                >
                  {t('auth.businessEntrepreneur')}
                </button>
                <button
                  type="button"
                  onClick={() => setRole('provider')}
                  className="py-2.5 px-3 rounded-lg border-2 text-sm font-medium transition-all"
                  style={
                    role === 'provider'
                      ? { background: BRAND_RED, color: '#fff', borderColor: BRAND_RED }
                      : { background: '#fff', color: '#374151', borderColor: '#e5e7eb' }
                  }
                >
                  {t('auth.aiSpecialist')}
                </button>
              </div>
            </div>

            {/* Profession field — shown when a role URL param is present */}
            {roleCtx && (
              <div>
                <label htmlFor="profession" className="block text-sm font-medium text-gray-700 mb-1">
                  {roleCtx.professionLabel} <span style={{ color: BRAND_RED }}>*</span>
                </label>
                <input
                  type="text"
                  id="profession"
                  value={profession}
                  onChange={e => setProfession(e.target.value)}
                  placeholder={roleCtx.professionPlaceholder}
                  className={inputClass}
                  required
                />
              </div>
            )}

            {/* Acquisition source */}
            <div>
              <label htmlFor="acquisitionSource" className="block text-sm font-medium text-gray-700 mb-1">
                Comment avez-vous connu OpportunAI ? <span style={{ color: BRAND_RED }}>*</span>
              </label>
              <div className="relative">
                <select
                  id="acquisitionSource"
                  value={acquisitionSource}
                  onChange={e => setAcquisitionSource(e.target.value)}
                  className={inputClass + ' appearance-none pr-9'}
                  required
                >
                  <option value="">Sélectionnez une option</option>
                  {ACQUISITION_SOURCES.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              </div>
            </div>

            {/* Anti-bot: Math captcha challenge */}
            <div>
              <label htmlFor="captchaAnswer" className="block text-sm font-medium text-gray-700 mb-1">
                Vérification anti-bot <span style={{ color: BRAND_RED }}>*</span>
              </label>
              {captchaError ? (
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-red-600">Impossible de charger la vérification. Veuillez réessayer.</p>
                  <button
                    type="button"
                    onClick={generateCaptcha}
                    className="self-start text-sm font-medium text-white px-4 py-2 rounded-lg"
                    style={{ background: BRAND_RED }}
                  >
                    Réessayer
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-lg text-sm font-mono font-semibold text-gray-700 select-none min-w-[80px] text-center">
                    {captchaLoading ? '…' : captchaQuestion}
                  </div>
                  <span className="text-gray-500 font-medium">=</span>
                  <input
                    type="number"
                    id="captchaAnswer"
                    value={captchaAnswer}
                    onChange={e => setCaptchaAnswer(e.target.value)}
                    className={inputClass}
                    style={{ '--tw-ring-color': BRAND_RED } as React.CSSProperties}
                    required
                    placeholder="?"
                  />
                </div>
              )}
            </div>

            {/* Anti-bot: Honeypot field (hidden from real users) */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '-9999px',
                top: '-9999px',
                width: '1px',
                height: '1px',
                overflow: 'hidden',
              }}
            >
              <label htmlFor="website_url">Website URL</label>
              <input
                type="text"
                id="website_url"
                name="website_url"
                value={websiteUrl}
                onChange={e => setWebsiteUrl(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {/* Terms */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="terms"
                checked={acceptedTerms}
                onChange={e => setAcceptedTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300"
                style={{ accentColor: BRAND_RED }}
                required
              />
              <label htmlFor="terms" className="text-sm text-gray-600 leading-snug">
                {t('auth.acceptTerms')}{' '}
                <Link to="/terms-of-service" target="_blank" style={{ color: BRAND_RED }} className="hover:underline">
                  {t('auth.termsOfService')}
                </Link>
                {' '}{t('auth.and')}{' '}
                <Link to="/privacy-policy" target="_blank" style={{ color: BRAND_RED }} className="hover:underline">
                  {t('auth.privacyPolicy')}
                </Link>
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !acceptedTerms}
              className="w-full py-3 rounded-lg text-white font-semibold text-sm transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: BRAND_RED }}
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('auth.creatingAccount')}
                </>
              ) : (
                roleCtx ? roleCtx.submitLabel : t('auth.register')
              )}
            </button>
          </form>

          <p className="mt-5 text-center text-sm text-gray-500">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" style={{ color: BRAND_RED }} className="font-medium hover:underline">
              {t('auth.login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
